import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  collection, addDoc, deleteDoc, doc, serverTimestamp, query, orderBy, limit, onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import './Board.css';

// Board: Pinnwand für alle. Neueste zuerst, live per onSnapshot auf die
// letzten N Nachrichten (N wächst mit "Ältere laden"). Eigene Beiträge
// und – als Admin – alle Beiträge lassen sich löschen (Firestore-Rules).

const SEITE = 30;
const MAX_ZEICHEN = 2000;

const relativeZeit = (date) => {
  if (!date) return 'gerade eben';
  const sek = Math.round((Date.now() - date.getTime()) / 1000);
  if (sek < 60) return 'gerade eben';
  const min = Math.round(sek / 60);
  if (min < 60) return `vor ${min} min`;
  const std = Math.round(min / 60);
  if (std < 24) return `vor ${std} h`;
  const tage = Math.round(std / 24);
  if (tage < 7) return `vor ${tage} ${tage === 1 ? 'Tag' : 'Tagen'}`;
  return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function Board() {
  const { currentUser, userProfile } = useAuth();
  const uid = currentUser?.uid;
  const istAdmin = userProfile?.isAdmin === true;

  const [nachrichten, setNachrichten] = useState([]);
  const [laden, setLaden] = useState(true);
  const [anzahl, setAnzahl] = useState(SEITE);
  const [text, setText] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState('');
  const [loeschen, setLoeschen] = useState(null); // id mit offener Rückfrage
  const textarea = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'boardNachrichten'), orderBy('timestamp', 'desc'), limit(anzahl));
    const unsub = onSnapshot(q, snap => {
      setNachrichten(snap.docs.map(d => {
        const data = d.data();
        return {
          id: d.id,
          autor: data.autor || 'Unbekannt',
          autorId: data.autorId,
          text: data.text || '',
          zeit: data.timestamp?.toDate ? data.timestamp.toDate() : null,
          istAdmin: data.istAdmin === true
        };
      }));
      setLaden(false);
    }, error => {
      console.error('Board konnte nicht geladen werden:', error);
      setLaden(false);
    });
    return () => unsub();
  }, [anzahl]);

  const handleSenden = async () => {
    const sauber = text.trim();
    if (!sauber || sendet) return;
    setSendet(true);
    setFehler('');
    try {
      await addDoc(collection(db, 'boardNachrichten'), {
        autor: userProfile?.displayName || 'Unbekannt',
        autorId: uid,
        text: sauber.slice(0, MAX_ZEICHEN),
        timestamp: serverTimestamp(),
        istAdmin
      });
      setText('');
      textarea.current?.focus();
    } catch (error) {
      console.error('Senden fehlgeschlagen:', error);
      setFehler('Senden fehlgeschlagen: ' + error.message);
    } finally {
      setSendet(false);
    }
  };

  const handleLoeschen = async (id) => {
    try {
      await deleteDoc(doc(db, 'boardNachrichten', id));
    } catch (error) {
      console.error('Löschen fehlgeschlagen:', error);
      setFehler('Löschen fehlgeschlagen: ' + error.message);
    } finally {
      setLoeschen(null);
    }
  };

  const onKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') handleSenden();
  };

  return (
    <div className="seite board-seite">
      <h1>💬 Board</h1>
      <p className="seite__untertitel">Tipps, Sprüche, Ansagen – für alle sichtbar.</p>

      <div className="karte board-schreiben">
        <textarea
          ref={textarea}
          className="board-schreiben__text"
          value={text}
          onChange={e => setText(e.target.value.slice(0, MAX_ZEICHEN))}
          onKeyDown={onKey}
          placeholder="Was gibt's?"
          rows={3}
        />
        <div className="board-schreiben__zeile">
          <span className="board-schreiben__hinweis">
            {text.length > 0 ? `${text.length}/${MAX_ZEICHEN}` : 'Strg + Enter zum Senden'}
          </span>
          <button
            type="button"
            className="board-schreiben__button"
            onClick={handleSenden}
            disabled={sendet || text.trim().length === 0}
          >
            {sendet ? '⏳ Sende…' : 'Senden'}
          </button>
        </div>
        {fehler && <div className="board-fehler">{fehler}</div>}
      </div>

      {laden ? (
        <div className="karte karte--leer">⏳ Lade Board…</div>
      ) : nachrichten.length === 0 ? (
        <div className="karte karte--leer">Noch keine Beiträge. Mach den Anfang! 🎉</div>
      ) : (
        <div className="board-feed">
          {nachrichten.map(n => {
            const eigen = n.autorId === uid;
            const darfLoeschen = eigen || istAdmin;
            return (
              <article key={n.id} className={`board-beitrag${n.istAdmin ? ' board-beitrag--admin' : ''}${eigen ? ' board-beitrag--eigen' : ''}`}>
                <div className="board-beitrag__kopf">
                  <span className="board-beitrag__autor">
                    {n.istAdmin && <span title="Admin">👑 </span>}
                    {n.autor}{eigen && <span className="board-beitrag__du"> (du)</span>}
                  </span>
                  <span className="board-beitrag__zeit" title={n.zeit ? n.zeit.toLocaleString('de-DE') : ''}>
                    {relativeZeit(n.zeit)}
                  </span>
                  {darfLoeschen && (
                    loeschen === n.id ? (
                      <span className="board-beitrag__loeschen">
                        Löschen?
                        <button type="button" onClick={() => handleLoeschen(n.id)}>Ja</button>
                        <button type="button" onClick={() => setLoeschen(null)}>Nein</button>
                      </span>
                    ) : (
                      <button type="button" className="board-beitrag__icon" onClick={() => setLoeschen(n.id)} title="Beitrag löschen" aria-label="Beitrag löschen">🗑</button>
                    )
                  )}
                </div>
                <div className="board-beitrag__text">{n.text}</div>
              </article>
            );
          })}
          {nachrichten.length >= anzahl && (
            <button type="button" className="board-mehr" onClick={() => setAnzahl(a => a + SEITE)}>
              Ältere Beiträge laden
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default Board;
